import { LinkedInPaper, LinkedInRepo } from '../../../services/api'
import {
    FeedCard,
    FeedCardBody,
    FeedCardFooter,
    CardTitle,
    ClampedText,
    MutedText,
    BadgeRow,
    Chip,
    ExternalLink,
    compactCount,
    healthPalette,
    repoHref,
    repoIsGone,
    repoLabel,
} from './linkedinStyles'

/**
 * Index rows for the repo and paper views of the content list.
 *
 * These are not the old Repositorios/Papers tabs coming back. The full record —
 * README, commits, history, notes, assignments — lives in the one detail modal,
 * and these cards exist only so a repo or a paper can be *found*: by owner, by
 * name, by description, by language, by how much it is moving, by whether any
 * post mentions it at all. None of those questions is answerable from a list of
 * posts, and one whole class of row (a paper the repo agent extracted from a
 * repo's README, which no post ever mentioned) is not reachable from it either.
 */

const formatDate = (value?: string | null): string => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
}

const openOnEnter = (open: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        open()
    }
}

export const RepoCatalogCard: React.FC<{ repo: LinkedInRepo; onOpen: () => void }> = ({ repo, onOpen }) => {
    const gone = repoIsGone(repo)
    // post_count counts every attachment; linked_post_count counts the ones that
    // came from a URL its author wrote. The difference is how many were inferred
    // from a project name, and a repo whose mentions are all inferred is the one
    // row a wrong attribution produces.
    const inferred = Math.max(0, (repo.post_count || 0) - (repo.linked_post_count || 0))

    return (
        <FeedCard>
            <FeedCardBody
                role="button"
                tabIndex={0}
                aria-label={`Abrir detalle del repositorio ${repoLabel(repo)}`}
                style={{ cursor: 'pointer' }}
                onClick={onOpen}
                onKeyDown={openOnEnter(onOpen)}
            >
                <CardTitle>💻 {repoLabel(repo)}</CardTitle>
                <BadgeRow>
                    {gone && <Chip $bg="#ECEFF1" $color="#607D8B">Ya no existe en GitHub</Chip>}
                    {repo.language && <Chip $bg="#EDE7F6" $color="#5E35B1">{repo.language}</Chip>}
                    {typeof repo.stars === 'number' && (
                        <Chip $bg="#FFF8E1" $color="#F9A825">⭐ {compactCount(repo.stars)}</Chip>
                    )}
                    {repo.is_trending && !gone && <Chip $bg="#FCE4EC" $color="#C2185B">🔥 Tendencia</Chip>}
                    {typeof repo.health_score === 'number' && (
                        // Transient props, not a spread: healthPalette returns
                        // {bg, color} and Chip reads {$bg, $color}, so spreading
                        // it painted every score with the neutral theme fallback
                        // and threw the green/amber/red verdict away — a failing
                        // repo looked exactly like a healthy one.
                        <Chip
                            $bg={healthPalette(repo.health_score).bg}
                            $color={healthPalette(repo.health_score).color}
                        >
                            Salud {repo.health_score}
                        </Chip>
                    )}
                    {!repo.has_post && <Chip $bg="#ECEFF1" $color="#607D8B">Sin publicación</Chip>}
                    {inferred > 0 && (
                        <Chip
                            $bg="#FFF3E0"
                            $color="#EF6C00"
                            title="Atribuciones deducidas del nombre del proyecto, no de un enlace"
                        >
                            {inferred} deducida{inferred === 1 ? '' : 's'}
                        </Chip>
                    )}
                </BadgeRow>
                {(repo.ai_description || repo.description) && (
                    <ClampedText $lines={3}>{repo.ai_description || repo.description}</ClampedText>
                )}
            </FeedCardBody>
            <FeedCardFooter>
                <MutedText>
                    {repo.post_count || 0} publicación{(repo.post_count || 0) === 1 ? '' : 'es'}
                </MutedText>
                {/* A repo GitHub no longer serves has nothing to open: the link is a
                    404, and offering it is what made a deleted repo read as live. */}
                {!gone && (
                    <ExternalLink
                        href={repoHref(repo)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Ver repositorio ↗
                    </ExternalLink>
                )}
            </FeedCardFooter>
        </FeedCard>
    )
}

export const PaperCatalogCard: React.FC<{ paper: LinkedInPaper; onOpen: () => void }> = ({ paper, onOpen }) => (
    <FeedCard>
        <FeedCardBody
            role="button"
            tabIndex={0}
            aria-label={`Abrir detalle del paper ${paper.title}`}
            style={{ cursor: 'pointer' }}
            onClick={onOpen}
            onKeyDown={openOnEnter(onOpen)}
        >
            <CardTitle>📄 {paper.title}</CardTitle>
            <BadgeRow>
                {paper.source && <Chip $bg="#E3F2FD" $color="#1565C0">{paper.source}</Chip>}
                {paper.external_id && <Chip $bg="#ECEFF1" $color="#546E7A">{paper.external_id}</Chip>}
                {/* The class the post list cannot reach at all: the repo agent found
                    this paper in a repo's README, and no post ever mentioned it. */}
                {!paper.has_post && (
                    <Chip $bg="#FFF3E0" $color="#EF6C00" title="Encontrado en el README de un repositorio">
                        Sin publicación
                    </Chip>
                )}
                {paper.repo_owner && paper.repo_name && (
                    <Chip $bg="#E8F5E9" $color="#2E7D32">
                        💻 {paper.repo_owner}/{paper.repo_name}
                    </Chip>
                )}
            </BadgeRow>
        </FeedCardBody>
        <FeedCardFooter>
            <MutedText>{formatDate(paper.created_at)}</MutedText>
            {paper.url && (
                <ExternalLink
                    href={paper.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    Ver paper ↗
                </ExternalLink>
            )}
        </FeedCardFooter>
    </FeedCard>
)
